import { memo, useCallback } from 'react';
import { cn } from '@divlab/divanui';

import { useMeta } from '@Queries/Meta';
import { useDeps } from '@Contexts/DI';
import useSearchParams from '@Hooks/useSearchParams';
import useNavigation from '@Navigation/useNavigation';
import Link from '@Navigation/Link';
import styles from './ButtonTab.module.css';

import type { FC, HTMLAttributes, MouseEvent, PropsWithChildren } from 'react';
import type { ConfiguratorOptionItemSize } from '@Pages/PageProduct/typings';

export interface ButtonTabProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: ConfiguratorOptionItemSize;
  tabName: string;
  onClickItem?: (e: MouseEvent, item: ConfiguratorOptionItemSize) => void;
}

export interface ContainerProps {
  className?: string;
  item: ConfiguratorOptionItemSize;
  tabName: string;
  onClick?: (e: MouseEvent) => void;
}

const Container: FC<PropsWithChildren<ContainerProps>> = (props) => {
  const { item, tabName, onClick, ...restProps } = props;
  const { analytics } = useDeps();
  const { navigate } = useNavigation();
  const meta = useMeta();
  const searchParams = useSearchParams();
  const oldHash = searchParams.get('hash');
  const { region } = meta.data;

  const handleClickLink = useCallback(() => {
    if (item.disabled || item.calcDisabled || item.selected) return;
    const url = oldHash ? `${region.url}${item.link}&hash=${oldHash}` : `${region.url}${item.link}`;

    navigate(url, { preventScrollReset: true, replace: true });

    analytics.dispatchEvent('constructor.productPage.parameterChange', {
      tabName,
      selectedValue: item.title,
    });
  }, [item, oldHash, region.url, navigate, analytics, tabName]);

  return item.link ? (
    <Link {...restProps} onClick={handleClickLink} />
  ) : (
    <div {...restProps} onClick={onClick} />
  );
};

const ButtonTab: FC<ButtonTabProps> = (props) => {
  const { className, item, tabName, onClickItem, ...restProps } = props;

  const handleClickContainer = useCallback(
    (e: MouseEvent) => {
      if (item.disabled || item.calcDisabled || item.selected) return;
      if (onClickItem) onClickItem(e, item);
    },
    [item, onClickItem],
  );

  return (
    <Container
      {...restProps}
      className={cn(
        styles.button,
        { [styles.selected]: item.selected },
        { [styles.disabled]: item.disabled || item.calcDisabled },
        className,
      )}
      item={item}
      tabName={tabName}
      onClick={handleClickContainer}
    >
      {item.title}
    </Container>
  );
};

export default memo(ButtonTab);
